import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  clearToken,
  me,
  setCurrentEstablishment,
  setCurrentTenant,
} from "../lib/api";
import { canSeeNav, DASHBOARD, NAV_SECTIONS, ROLE_LABEL, type NavItem } from "../lib/navigation";
import { permissionsOfMe } from "../lib/permissions";
import { useIdleLogout } from "../lib/useIdleLogout";
import { TenantSwitcher } from "./TenantSwitcher";
import { EstablishmentSwitcher } from "./EstablishmentSwitcher";
import { CommandPalette } from "./CommandPalette";
import { ErrorBoundary } from "./ErrorBoundary";
import { RouteGuard } from "./RouteGuard";
import { ADMIN_VERSION, BUILD_DATE, BUILD_SHA } from "../version";

const NAV_OPEN_KEY = "alto_admin_nav_open";

function loadOpenState(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(NAV_OPEN_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function Layout() {
  const navigate = useNavigate();
  // Deconnexion auto apres 1 h d'inactivite (renvoie vers /login proprement).
  useIdleLogout();
  const meQuery = useQuery({ queryKey: ["admin", "me"], queryFn: me });

  // Etat ouvert/ferme par section, persiste en localStorage. Le default
  // (defaultOpen de lib/navigation.ts) s'applique si pas encore stocke.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const stored = loadOpenState();
    const initial: Record<string, boolean> = {};
    for (const section of NAV_SECTIONS) {
      initial[section.title] = stored[section.title] ?? section.defaultOpen;
    }
    return initial;
  });

  // Sprint X-7 : drawer mobile (md = breakpoint laptop)
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(openSections));
    } catch {
      /* quota plein ou mode privé : on ignore */
    }
  }, [openSections]);

  const toggleSection = (title: string) =>
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));

  const handleLogout = () => {
    clearToken();
    setCurrentTenant(null);
    setCurrentEstablishment(null);
    navigate("/login", { replace: true });
  };

  // V1.51 : le menu suit les droits envoyés par le serveur (Gestion des droits).
  const perms = useMemo(() => permissionsOfMe(meQuery.data), [meQuery.data]);
  const canSee = (item: NavItem) => canSeeNav(item, perms);

  return (
    // R-2 : h-screen au lieu de min-h-screen pour que main ait sa propre scrollbar
    // et que aside reste sticky en haut (sinon le scroll du main fait descendre
    // aside et on perd la nav quand on scrolle).
    // X-7 : sur mobile (< md), grid 1 colonne + aside en drawer absolute,
    // sur md+ grid 2 colonnes classique.
    <div className="h-screen md:grid md:grid-cols-[280px_1fr] overflow-hidden relative">
      {/* X-7 : header mobile avec hamburger */}
      <header className="md:hidden flex items-center justify-between px-4 h-14 bg-white border-b border-[color:var(--color-alto-border)] sticky top-0 z-30">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Ouvrir le menu"
          className="p-2 -ml-2 text-[color:var(--color-alto-ink)]"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <p className="text-xs tracking-widest text-[color:var(--color-alto-muted)] uppercase">
          ALTO ONSITE
        </p>
        <button
          type="button"
          onClick={() => {
            const ev = new KeyboardEvent("keydown", {
              key: "k",
              metaKey: true,
              bubbles: true,
            });
            window.dispatchEvent(ev);
          }}
          aria-label="Recherche globale"
          className="p-2 -mr-2 text-[color:var(--color-alto-muted)]"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </header>

      {/* X-7 : backdrop drawer mobile */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Fermer le menu"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/40"
        />
      )}

      <aside
        onClick={(e) => {
          // Auto-close drawer when a NavLink is clicked (delegated)
          const target = e.target as HTMLElement;
          if (target.closest("a[href]")) setMobileOpen(false);
        }}
        className={
          "bg-white border-r border-[color:var(--color-alto-border)] flex flex-col h-screen overflow-hidden " +
          // X-7 : drawer mobile slide depuis la gauche
          "fixed inset-y-0 left-0 w-[280px] z-50 transition-transform md:translate-x-0 md:static " +
          (mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0")
        }
      >
        <div className="p-5 border-b border-[color:var(--color-alto-border)]">
          <p className="text-xs tracking-widest text-[color:var(--color-alto-muted)] uppercase">
            ALTO ONSITE
          </p>
          <h1 className="font-[family-name:var(--font-display)] text-xl mt-1 mb-1 flex items-baseline gap-2">
            <span>Back-office</span>
            {/* Sprint CT-3 : version figee (V0.98 jusqu'au lancement public). */}
            <span
              className="text-[11px] font-sans font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-[color:var(--color-alto-accent)] text-white"
              title={`Build ${BUILD_SHA} — ${BUILD_DATE} UTC`}
            >
              {ADMIN_VERSION}
            </span>
          </h1>
          {/* Indicateur de build (commit + date) : confirme un deploiement sans
              toucher au numero de version fige. */}
          <p
            className="text-[10px] font-mono text-[color:var(--color-alto-muted)] mb-4"
            title="Commit et date du build en cours"
          >
            build {BUILD_SHA} · {BUILD_DATE} UTC
          </p>
          <div className="space-y-2">
            <TenantSwitcher />
            <EstablishmentSwitcher />
            {/* Sprint X-2 : hint Cmd+K (la palette s'ouvre via le raccourci clavier) */}
            <button
              type="button"
              onClick={() => {
                // Simule un Cmd+K — la palette ecoute window keydown
                const ev = new KeyboardEvent("keydown", {
                  key: "k",
                  metaKey: true,
                  bubbles: true,
                });
                window.dispatchEvent(ev);
              }}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-[color:var(--color-alto-muted)] hover:text-[color:var(--color-alto-ink)] rounded-md border border-[color:var(--color-alto-border)] bg-[color:var(--color-alto-bg)] hover:bg-white transition-colors"
              title="Recherche globale (Cmd+K)"
            >
              <span className="flex items-center gap-2">
                <span aria-hidden>⌕</span>
                <span>Rechercher…</span>
              </span>
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-[color:var(--color-alto-border)]">
                ⌘K
              </span>
            </button>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-2 overflow-y-auto" aria-label="Navigation principale">
          {/* Sprint W : lien pinné Tableau de bord, toujours visible en haut.
              Le Guide, épinglé jusqu'en V1.24, est dans la section Aide. */}
          <NavLink
            to={DASHBOARD.to}
            end
            className={({ isActive }) =>
              "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors " +
              (isActive
                ? "bg-[color:var(--color-alto-ink)] text-white"
                : "text-[color:var(--color-alto-ink)] hover:bg-[color:var(--color-alto-bg)]")
            }
          >
            <span aria-hidden>📊</span>
            <span>{DASHBOARD.label}</span>
          </NavLink>
          {NAV_SECTIONS.map((section) => {
            const visibleItems = section.items.filter(canSee);
            if (visibleItems.length === 0) return null;
            const isOpen = openSections[section.title] ?? section.defaultOpen;
            return (
              <div key={section.title}>
                <button
                  type="button"
                  onClick={() => toggleSection(section.title)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-wider text-[color:var(--color-alto-muted)] font-medium hover:text-[color:var(--color-alto-ink)] transition-colors"
                >
                  <span>{section.title}</span>
                  <svg
                    aria-hidden
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    className={"transition-transform " + (isOpen ? "rotate-180" : "")}
                  >
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  </svg>
                </button>
                {isOpen && (
                  <div className="space-y-0.5 mt-1">
                    {visibleItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          "flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors " +
                          (isActive
                            ? "bg-[color:var(--color-alto-ink)] text-white"
                            : "text-[color:var(--color-alto-ink)] hover:bg-[color:var(--color-alto-bg)]")
                        }
                      >
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-[color:var(--color-alto-border)] space-y-2">
          {meQuery.data && (
            <NavLink
              to="/account"
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                "block px-3 py-1.5 rounded-md transition-colors " +
                (isActive
                  ? "bg-[color:var(--color-alto-bg)]"
                  : "hover:bg-[color:var(--color-alto-bg)]")
              }
              title="Mon compte"
            >
              <p className="text-sm font-medium truncate">{meQuery.data.displayName}</p>
              <p className="text-xs text-[color:var(--color-alto-muted)] truncate">
                {meQuery.data.email}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[color:var(--color-alto-muted)] mt-0.5">
                {ROLE_LABEL[meQuery.data.role] ?? meQuery.data.role} · Mon compte
              </p>
            </NavLink>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-left px-3 py-2 text-sm rounded-md text-[color:var(--color-alto-muted)] hover:bg-[color:var(--color-alto-bg)] hover:text-[color:var(--color-alto-ink)] transition-colors"
          >
            Se déconnecter
          </button>
        </div>
      </aside>
      <main className="overflow-y-auto h-[calc(100vh-3.5rem)] md:h-screen">
        {/* Sprint CT-3 : ErrorBoundary autour de l'Outlet pour preserver la
            sidebar quand une page enfant crash (donnees manquantes, erreur de
            rendu). Sans ca, le crash demonte le Layout entier. */}
        <ErrorBoundary>
          {/* Palier 2 (V1.30) : une page cachée du menu ne s'ouvre pas par son adresse. */}
          <RouteGuard>
            <Outlet />
          </RouteGuard>
        </ErrorBoundary>
      </main>
      {/* Sprint X-2 : palette globale, ecoute Cmd+K partout sous Layout */}
      <CommandPalette />
    </div>
  );
}
